/**
 * @fileoverview Provides utility functions for determining cursor position after text changes, focusing on single-line edits. Includes functions to analyze line differences and calculate the final editing location based on character-level changes.
 */

import { diffChars } from 'diff'

export type Position = {
  row: number
  column: number
}

/**
 * Algorithm:
 * - verify it is a single line change
 * - on the changed line, find the final editted location and assume that's where the user finished editing
 */
export function pickCursorFromLineDiff(changes: Diff.Change[]): Position | undefined {
  if (!isDiffWithOnlyOneAdditionOrEdit(changes)) {
    return undefined
  }

  if (changes.length === 3) {
    const changeLines = changes[1]!.value.trim().split('\n')
    return { row: changes[0]!.count!, column: changeLines[changeLines.length - 1]!.length }
  } else {
    const diffCharsResult = diffChars(changes[1]!.value, changes[2]!.value)
    const positionWithinDiff = countToEndOfLastAddedOrRemove(diffCharsResult)
    return {
      row: changes[0]!.count! + positionWithinDiff.row,
      column: positionWithinDiff.column,
    }
  }
}

function countToEndOfLastAddedOrRemove(charDiff: Diff.Change[]): { row: number; column: number } {
  let count = 0
  let lastAddedOrRemovedIndex = -1
  charDiff.forEach((change, index) => {
    if (change.added || change.removed) {
      lastAddedOrRemovedIndex = index
    }
  })
  for (let i = 0; i <= lastAddedOrRemovedIndex; i++) {
    const change = charDiff[i]!
    if (!change.removed) {
      count += change.count || 0
    }
  }

  const fullString = charDiff.map((c) => (c.removed ? '' : c.value)).join('')
  const lines = fullString.split('\n')
  let row = 0
  while (count > lines[row]!.length) {
    count = count - lines[row]!.length - 1 // Extra 1 for the newline
    row++
  }

  return { row, column: count }
}

export function isDiffWithOnlyOneAdditionOrEdit(changes: Diff.Change[]): boolean {
  if (changes.length !== 3 && changes.length !== 4) {
    return false
  }

  const isNoChanges = (c: Diff.Change | undefined): boolean => !!c && !c.added && !c.removed

  if (changes.length === 3) {
    // Should be: unchanged, added, unchanged
    return isNoChanges(changes[0]) && !!changes[1]!.added && isNoChanges(changes[2])
  } else {
    // Should be: unchanged, removed, added, unchanged
    return (
      isNoChanges(changes[0]) &&
      !!changes[1]!.removed &&
      !!changes[2]!.added &&
      isNoChanges(changes[3])
    )
  }
}
